/** TMUA workspace scoring: P1+P2 raw → % → grade band, shown as x.x/9.0 */

import {
  clearPaperScores,
  loadPaperScores,
  readUserCache,
  savePaperScore,
  writeUserCache,
} from './paperProgressCache';

export const TMUA_SCORES_KEY = 'tmua_paper_scores';
export const TMUA_TARGET_KEY = 'tmua_workspace_target';

/**
 * Percent correct → integer grade band (monotonic).
 * ≥90→7, 80–89→6, 70–79→5, 60–69→4, 50–59→3, 40–49→2, <40→1
 */
export function percentToTmuaGrade(pct) {
  const p = Number(pct);
  if (!Number.isFinite(p)) return null;
  if (p >= 90) return 7;
  if (p >= 80) return 6;
  if (p >= 70) return 5;
  if (p >= 60) return 4;
  if (p >= 50) return 3;
  if (p >= 40) return 2;
  return 1;
}

export function gradeFromCorrectTotal(correct, total) {
  const t = Number(total);
  if (!t || t <= 0) return null;
  const c = Math.max(0, Number(correct) || 0);
  return percentToTmuaGrade((c / t) * 100);
}

export function formatTmuaGrade(grade) {
  if (grade == null || !Number.isFinite(Number(grade))) return '—';
  return `${Number(grade).toFixed(1)}/9.0`;
}

export function formatPaperRaw(correct, total) {
  if (total == null || total <= 0 || correct == null) return '—';
  return `${correct}/${total}`;
}

export function loadTmuaPaperScores(userId) {
  return loadPaperScores(TMUA_SCORES_KEY, userId);
}

export function saveTmuaPaperScore(userId, paperId, score = {}) {
  savePaperScore(TMUA_SCORES_KEY, userId, paperId, score);
}

export function clearTmuaPaperScores(userId, paperIds = []) {
  clearPaperScores(TMUA_SCORES_KEY, userId, paperIds);
}

/** Forget the user's cached scores for a sitting's papers (a retake). */
export function resetTmuaSittingProgress(userId, paperIds = []) {
  clearTmuaPaperScores(userId, paperIds);
}

export function sittingScoreFromPapers(paper1Id, paper2Id, scoresMap = {}) {
  const s1 = paper1Id ? scoresMap[String(paper1Id)] : null;
  const s2 = paper2Id ? scoresMap[String(paper2Id)] : null;
  if (!s1 && !s2) return { p1: null, p2: null, grade: null, mode: null, status: 'none' };

  const p1Done = Boolean(s1 && s1.total > 0);
  const p2Done = Boolean(s2 && s2.total > 0);
  const both = p1Done && p2Done;
  const either = p1Done || p2Done;

  let grade = null;
  if (both) {
    grade = gradeFromCorrectTotal(
      (s1.correct || 0) + (s2.correct || 0),
      (s1.total || 0) + (s2.total || 0)
    );
  }

  const mode = s1?.mode || s2?.mode || null;
  const status = both ? 'done' : either ? 'progress' : 'none';

  return {
    p1: p1Done ? s1 : null,
    p2: p2Done ? s2 : null,
    grade,
    mode,
    status,
  };
}

export function loadTmuaTarget(userId) {
  const fallback = { value: 6.0, label: 'LSE — Economics / Data Science', custom: false };
  return readUserCache(TMUA_TARGET_KEY, userId, fallback);
}

export function saveTmuaTarget(userId, target) {
  writeUserCache(TMUA_TARGET_KEY, userId, target);
}
